import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Search, Download, ChevronLeft, ChevronRight, X } from 'lucide-react';

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-yellow-100 text-yellow-700',
  booked: 'bg-purple-100 text-purple-700',
  converted: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-600',
};

const TYPE_LABELS = {
  booking_call: 'Book a Call',
  business: 'Business',
  workshop: 'Workshop',
  speaking: 'Speaking',
  personal_dev: 'Personal Dev',
};

const STATUSES = ['new', 'contacted', 'booked', 'converted', 'closed'];

export default function InquiriesPage() {
  const { api } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ inquiry_type: '', status: '', search: '' });
  const [selected, setSelected] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteMessage, setNoteMessage] = useState('');

  const fetchData = useCallback(async (page = 1) => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 15 });
    if (filters.inquiry_type) params.set('inquiry_type', filters.inquiry_type);
    if (filters.status) params.set('status', filters.status);
    if (filters.search) params.set('search', filters.search);
    try {
      const { data: d } = await api.get(`/admin/inquiries?${params}`);
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [api, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateStatus = async (id, status) => {
    await api.put(`/admin/inquiries/${id}`, { status });
    if (selected?.id === id) setSelected((p) => ({ ...p, status }));
    fetchData(data.page);
  };

  const addNote = async () => {
    if (!selected || !newNote.trim()) return;
    setNoteSaving(true);
    setNoteMessage('');
    try {
      const { data: res } = await api.post(`/admin/inquiries/${selected.id}/notes`, { text: newNote.trim() });
      setSelected((p) => ({ ...p, note_history: res.note_history }));
      setNewNote('');
      setNoteMessage('Note saved');
      setTimeout(() => setNoteMessage(''), 3000);
    } catch {
      setNoteMessage('Failed to save note');
    } finally {
      setNoteSaving(false);
    }
  };

  const exportCsv = async () => {
    const { data: blob } = await api.get('/admin/inquiries/export/csv', { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([blob]));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'inquiries.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      <div data-testid="admin-inquiries">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-medium" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Inquiries</h1>
            <p className="text-sm text-gray-500">Manage leads and contact form submissions</p>
          </div>
          <button onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 transition-colors" data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-5" data-testid="inquiry-filters">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search name or email..."
              value={filters.search}
              onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
              className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C4A484]"
              data-testid="inquiry-search"
            />
          </div>
          <select
            value={filters.inquiry_type}
            onChange={(e) => setFilters((p) => ({ ...p, inquiry_type: e.target.value }))}
            className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#C4A484]"
            data-testid="inquiry-type-filter"
          >
            <option value="">All Types</option>
            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
            className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#C4A484]"
            data-testid="inquiry-status-filter"
          >
            <option value="">All Statuses</option>
            {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#C4A484] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : data.items.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">No inquiries found</div>
          ) : (
            <table className="w-full text-sm" data-testid="inquiries-table">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Name</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Type</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Status</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Date</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50/50 transition-colors" data-testid={`inquiry-row-${item.id}`}>
                    <td className="px-5 py-3.5">
                      <p className="font-medium text-gray-900">{item.name}</p>
                      <p className="text-xs text-gray-400">{item.email}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-xs bg-gray-100 px-2.5 py-1 rounded-full capitalize">{TYPE_LABELS[item.inquiry_type] || item.inquiry_type}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <select
                        value={item.status}
                        onChange={(e) => updateStatus(item.id, e.target.value)}
                        className={`text-xs px-2.5 py-1 rounded-full border-0 cursor-pointer ${STATUS_COLORS[item.status] || 'bg-gray-100'}`}
                        data-testid={`status-select-${item.id}`}
                      >
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-5 py-3.5 text-xs text-gray-400">
                      {new Date(item.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3.5">
                      <button
                        onClick={() => { setSelected(item); setNewNote(''); setNoteMessage(''); }}
                        className="text-xs text-[#C4A484] hover:underline"
                        data-testid={`view-inquiry-${item.id}`}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {data.pages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
              <span className="text-xs text-gray-400">Page {data.page} of {data.pages} ({data.total} total)</span>
              <div className="flex gap-2">
                <button onClick={() => fetchData(data.page - 1)} disabled={data.page <= 1} className="p-1.5 border border-gray-200 rounded-lg disabled:opacity-30 hover:bg-gray-50">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button onClick={() => fetchData(data.page + 1)} disabled={data.page >= data.pages} className="p-1.5 border border-gray-200 rounded-lg disabled:opacity-30 hover:bg-gray-50">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Detail Drawer */}
        {selected && (
          <div className="fixed inset-0 z-50 flex justify-end" data-testid="inquiry-detail-drawer">
            <div className="absolute inset-0 bg-black/30" onClick={() => setSelected(null)} />
            <div className="relative w-full max-w-md bg-white h-full shadow-xl overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                <h2 className="text-lg font-medium">Inquiry Details</h2>
                <button onClick={() => setSelected(null)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-xs text-gray-400 mb-1">Name</p><p className="text-sm font-medium">{selected.name}</p></div>
                  <div><p className="text-xs text-gray-400 mb-1">Email</p><p className="text-sm">{selected.email}</p></div>
                  <div><p className="text-xs text-gray-400 mb-1">Phone</p><p className="text-sm">{selected.phone || '—'}</p></div>
                  <div><p className="text-xs text-gray-400 mb-1">Type</p><p className="text-sm capitalize">{TYPE_LABELS[selected.inquiry_type] || selected.inquiry_type}</p></div>
                  <div><p className="text-xs text-gray-400 mb-1">Source</p><p className="text-sm capitalize">{selected.source_page || '—'}</p></div>
                  <div><p className="text-xs text-gray-400 mb-1">Date</p><p className="text-sm">{new Date(selected.created_at).toLocaleString()}</p></div>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Status</p>
                  <div className="flex gap-2 flex-wrap">
                    {STATUSES.map((s) => (
                      <button
                        key={s}
                        onClick={() => updateStatus(selected.id, s)}
                        className={`text-xs px-3 py-1.5 rounded-full capitalize transition-colors ${selected.status === s ? STATUS_COLORS[s] : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}`}
                        data-testid={`drawer-status-${s}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Message</p>
                  <p className="text-sm bg-gray-50 rounded-lg p-3">{selected.message || 'No message provided'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-3">Notes</p>
                  <div className="flex gap-2 mb-2">
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows={2}
                      placeholder="Add a note..."
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C4A484] resize-none"
                      data-testid="inquiry-note-input"
                    />
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <button
                      onClick={addNote}
                      disabled={noteSaving || !newNote.trim()}
                      className="px-4 py-2 bg-[#1A1A1A] text-white text-xs font-medium rounded-lg hover:bg-[#C4A484] transition-colors disabled:opacity-40"
                      data-testid="add-note-btn"
                    >
                      {noteSaving ? 'Saving...' : 'Add Note'}
                    </button>
                    {noteMessage && (
                      <span className={`text-xs ${noteMessage.includes('Failed') ? 'text-red-500' : 'text-green-600'}`} data-testid="note-save-message">
                        {noteMessage}
                      </span>
                    )}
                  </div>
                  {/* Note Timeline */}
                  <div className="space-y-0" data-testid="note-history">
                    {(selected.note_history || []).length === 0 && !selected.notes && (
                      <p className="text-xs text-gray-400 italic">No notes yet</p>
                    )}
                    {/* Legacy note (if exists and no note_history) */}
                    {selected.notes && !(selected.note_history || []).length && (
                      <div className="relative pl-4 pb-4 border-l-2 border-gray-200">
                        <div className="absolute -left-[5px] top-0 w-2 h-2 rounded-full bg-gray-300" />
                        <p className="text-sm text-gray-700">{selected.notes}</p>
                        <p className="text-[10px] text-gray-400 mt-1">Legacy note</p>
                      </div>
                    )}
                    {[...(selected.note_history || [])].reverse().map((note, i) => (
                      <div key={`note-${i}`} className="relative pl-4 pb-4 border-l-2 border-[#C4A484]/30 last:border-l-0 last:pb-0">
                        <div className="absolute -left-[5px] top-0 w-2 h-2 rounded-full bg-[#C4A484]" />
                        <p className="text-sm text-gray-700">{note.text}</p>
                        <p className="text-[10px] text-gray-400 mt-1">
                          {new Date(note.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
